import { useState, useEffect, useCallback } from 'react';
import { UserActionsService, UserFeatureUsage, UserWorkflowProgress } from '../services/userActions';

interface UseUserActionsReturn {
  // State
  featureUsage: UserFeatureUsage[];
  workflowProgress: UserWorkflowProgress[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  trackFeatureUsage: (featureName: string, metadata?: Record<string, any>) => Promise<void>;
  trackStepInitiation: (stepId: string, stepName: string, workflowName?: string) => Promise<void>;
  trackStepCompletion: (stepId: string, stepName: string, workflowName?: string) => Promise<void>;
  trackWorkflowCompletion: (workflowName: string, metadata?: Record<string, any>) => Promise<void>;
  
  // Queries
  hasUsedFeature: (featureName: string) => boolean;
  hasInitiatedStep: (stepId: string, workflowName?: string) => Promise<boolean>;
  getWorkflowProgress: (workflowName?: string) => Promise<{
    hasProjects: boolean;
    hasJobDescriptions: boolean;
    hasAnalyses: boolean;
    hasViewedMarketData: boolean;
  }>;
  
  // Utilities
  refresh: () => Promise<void>;
  migrateLegacyData: () => Promise<void>;
}

export function useUserActions(): UseUserActionsReturn {
  const [featureUsage, setFeatureUsage] = useState<UserFeatureUsage[]>([]);
  const [workflowProgress, setWorkflowProgress] = useState<UserWorkflowProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [featureUsageData, workflowProgressData] = await Promise.all([
        UserActionsService.getUserFeatureUsage(),
        UserActionsService.getUserWorkflowProgress()
      ]);
      
      setFeatureUsage(featureUsageData);
      setWorkflowProgress(workflowProgressData);
    } catch (err) {
      console.error('Error loading user actions data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user actions data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Track feature usage
  const trackFeatureUsage = useCallback(async (featureName: string, metadata: Record<string, any> = {}) => {
    try {
      setError(null);
      await UserActionsService.trackFeatureUsage(featureName, metadata);
      // Refresh data to get updated state
      await loadData();
    } catch (err) {
      console.error('Error tracking feature usage:', err);
      setError(err instanceof Error ? err.message : 'Failed to track feature usage');
    }
  }, [loadData]);

  // Track step initiation
  const trackStepInitiation = useCallback(async (stepId: string, stepName: string, workflowName: string = 'hiring-project') => {
    try {
      setError(null);
      await UserActionsService.trackStepInitiation(stepId, stepName, workflowName);
      // Refresh data to get updated state
      await loadData();
    } catch (err) {
      console.error('Error tracking step initiation:', err);
      setError(err instanceof Error ? err.message : 'Failed to track step initiation');
    }
  }, [loadData]);

  // Track step completion
  const trackStepCompletion = useCallback(async (stepId: string, stepName: string, workflowName: string = 'hiring-project') => {
    try {
      setError(null);
      await UserActionsService.trackStepCompletion(stepId, stepName, workflowName);
      // Refresh data to get updated state
      await loadData();
    } catch (err) {
      console.error('Error tracking step completion:', err);
      setError(err instanceof Error ? err.message : 'Failed to track step completion');
    }
  }, [loadData]);

  // Track workflow completion
  const trackWorkflowCompletion = useCallback(async (workflowName: string, metadata: Record<string, any> = {}) => {
    try {
      setError(null);
      await UserActionsService.trackWorkflowCompletion(workflowName, metadata);
      // Refresh data to get updated state
      await loadData();
    } catch (err) {
      console.error('Error tracking workflow completion:', err);
      setError(err instanceof Error ? err.message : 'Failed to track workflow completion');
    }
  }, [loadData]);

  // Check if user has used a feature (from cached data)
  const hasUsedFeature = useCallback((featureName: string): boolean => {
    return featureUsage.some(usage => usage.feature_name === featureName && usage.has_used);
  }, [featureUsage]);

  // Check if user has initiated a step (requires API call)
  const hasInitiatedStep = useCallback(async (stepId: string, workflowName: string = 'hiring-project'): Promise<boolean> => {
    try {
      return await UserActionsService.hasInitiatedStep(stepId, workflowName);
    } catch (err) {
      console.error('Error checking step initiation:', err);
      return false;
    }
  }, []);

  // Get workflow progress
  const getWorkflowProgress = useCallback(async (workflowName?: string) => {
    try {
      return await UserActionsService.getWorkflowProgress(workflowName);
    } catch (err) {
      console.error('Error getting workflow progress:', err);
      return {
        hasProjects: false,
        hasJobDescriptions: false,
        hasAnalyses: false,
        hasViewedMarketData: false
      };
    }
  }, []);

  // Refresh data
  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  // Migrate legacy localStorage data
  const migrateLegacyData = useCallback(async () => {
    try {
      setError(null);
      await UserActionsService.migrateLegacyData();
      // Refresh data to get updated state
      await loadData();
    } catch (err) {
      console.error('Error migrating legacy data:', err);
      setError(err instanceof Error ? err.message : 'Failed to migrate legacy data');
    }
  }, [loadData]);

  return {
    // State
    featureUsage,
    workflowProgress,
    isLoading,
    error,
    
    // Actions
    trackFeatureUsage,
    trackStepInitiation,
    trackStepCompletion,
    trackWorkflowCompletion,
    
    // Queries
    hasUsedFeature,
    hasInitiatedStep,
    getWorkflowProgress,
    
    // Utilities
    refresh,
    migrateLegacyData
  };
}

export default useUserActions; 